import { setByPath } from "./reportPath";
export { parsePath, setByPath } from "./reportPath";
import { IReport } from "@mctypes/report.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { AppThunk } from "../../../store";
import { doGetSingleReportBoard, doUpdateReportBoardGeneralSettings, doUpdateReportData } from "./reportBoardAPI";
import { doGetReportTemplates } from "./reportTemplateAPI";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import { reportRTK } from "./reportRTK";
import { IReportTemplate, IReportKeyData, ReportCode } from "@mcapps/shared/reportSchemas/reportSchemas";

export interface IReportReducer {
  currentReportId: string | null;
  draftReportId: string | null;
  draftRevision: number | null;
  reportBoards: IReport.IReportBoardPopulated[];
  reportTemplates: IReportTemplate[];
  filterReportsByLoggedInUserChecked: boolean;
  editedData: IReportKeyData;
}

export const createEmptyEditedData = (): IReportKeyData => ({
  101: null,
  102: null,
  103: null,
  104: null,
  106: null,
  201: null,
  202: null,
  205: null,
  301: null,
  401: null,
  701: null,
  901: null,
  902: null,
});

const initialState: IReportReducer = {
  currentReportId: null,
  draftReportId: null,
  draftRevision: null,
  reportBoards: null,
  reportTemplates: null,
  filterReportsByLoggedInUserChecked: false,
  editedData: createEmptyEditedData(),
};

export const fetchAndSetSingleReportThunk =
  (reportBoardId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
  };

export const fetchAndSetReportTemplatesThunk = () => async (dispatch) => {
  const reportTemplates = await doGetReportTemplates();
  dispatch(setReportTemplatesInRedux(reportTemplates));
};

export const updateReportSettingsThunk =
  (reportId: string, attr, value): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    await doUpdateReportBoardGeneralSettings(project._id, reportId, attr, value);
    dispatch(fetchAndSetSingleReportThunk(reportId));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.SETTINGS_UPDATED));
  };

export const updateReportDataThunk =
  (reportId: string, templateId: number): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const editedData = getState().reportReducer.editedData[templateId as ReportCode];
    const expectedRevision = getState().reportReducer.draftRevision;
    if (getState().reportReducer.draftReportId !== reportId || expectedRevision == null)
      throw new Error("Rapporten er ikke klar for lagring. Last rapporten på nytt.");
    const saved = await doUpdateReportData(project._id, reportId, editedData, "", templateId, expectedRevision);
    dispatch(
      reportRTK.util.updateQueryData(
        "getSingleReportBoard",
        { projectId: project._id, reportBoardId: reportId },
        () => saved,
      ),
    );
    dispatch(reportDraftSaved({ reportId, revision: saved.revision }));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.SETTINGS_UPDATED));
  };

export const fetchReportAndUpdateUploadedFilesThunk =
  (reportId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const report = await doGetSingleReportBoard(project._id, reportId);
  };

// 2) Payload som låser data-typen basert på code
export type SetEditedDataPayload = {
  [C in ReportCode]: { code: C; data: IReportKeyData[C] };
}[ReportCode];
type SetEditedValuePayload = {
  code: ReportCode;
  path: string; // t.ex "generellInfo.utarbeidetAv" eller "items.0.title"
  value: unknown;
};

// reportPath.ts (eller direkt i reportSlice-filen)

export const reportSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    resetReportApp: (state) => {
      return initialState;
    },
    setReportTemplatesInRedux: (state, action: PayloadAction<IReportTemplate[]>) => {
      state.reportTemplates = action.payload;
    },
    setEditedDataInRedux: (state, action: PayloadAction<SetEditedDataPayload>) => {
      const { code, data } = action.payload;
      state.editedData[code as any] = data;
    },
    setEditedValueInRedux: (state, action: PayloadAction<SetEditedValuePayload>) => {
      const { code, path, value } = action.payload;
      if (state.editedData[code] == null) state.editedData[code] = {} as any;
      setByPath(state.editedData[code] as any, path, value); // samma setByPath som tidigare
    },
    setCurrentReportIdInRedux: (state, action: PayloadAction<string | null>) => {
      state.currentReportId = action.payload;
    },
    initializeReportDraft: (
      state,
      action: PayloadAction<{ reportId: string; code: ReportCode; data: object; revision: number }>,
    ) => {
      const { reportId, code, data, revision } = action.payload;
      // Refetches must not replace an open draft or advance its concurrency token.
      if (state.currentReportId !== reportId || state.draftReportId === reportId) return;
      state.draftReportId = reportId;
      state.draftRevision = revision;
      state.editedData[code] = data as any;
    },
    reportDraftSaved: (state, action: PayloadAction<{ reportId: string; revision: number }>) => {
      if (state.draftReportId === action.payload.reportId) state.draftRevision = action.payload.revision;
    },
    resetEditedDataInRedux: (state) => {
      state.draftReportId = null;
      state.draftRevision = null;
      state.editedData = createEmptyEditedData();
    },
  },
});

export const {
  resetReportApp,
  setReportTemplatesInRedux,
  setCurrentReportIdInRedux,
  setEditedDataInRedux,
  setEditedValueInRedux,
  resetEditedDataInRedux,
  initializeReportDraft,
  reportDraftSaved,
} = reportSlice.actions;

export default reportSlice.reducer;
